import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtTime } from "@/lib/format";
import { Flag, CheckCircle2 } from "lucide-react";

export default function MissionStatusCard({ droneId, onChanged }) {
  const [mission, setMission] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/drones/${droneId}/mission/active`);
      setMission(data.mission);
    } catch (e) { setMission(null); }
  }, [droneId]);

  useEffect(() => { load(); }, [load]);

  const complete = async () => {
    if (!mission) return;
    setBusy(true);
    try {
      await api.post(`/drones/${droneId}/mission/${mission.id}/complete`);
      toast.success("Mission terminée", { description: "Archivée pour analyse post-vol" });
      setMission(null);
      onChanged?.();
    } catch (e) { toast.error("Échec", { description: apiErr(e) }); }
    finally { setBusy(false); setConfirm(false); }
  };

  const active = !!mission;

  return (
    <div data-testid="mission-status-card" className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#525252]">État mission</div>
        <span data-testid="mission-status-badge" className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm"
          style={{ color: active ? "#3B82F6" : "#525252", border: `1px solid ${active ? "#3B82F633" : "#ffffff15"}` }}>
          {active ? "Mission active" : "Aucune mission active"}
        </span>
      </div>

      {active && (
        <div className="font-mono text-xs text-[#A3A3A3] space-y-0.5">
          <div className="flex items-center gap-1.5 text-white"><Flag size={12} className="text-[#3B82F6]" /> {mission.mission_type} → {mission.address?.slice(0, 40)}</div>
          <div className="text-[10px] text-[#525252]">Lancée {fmtTime(mission.created_at)} · {mission.distance_km} km</div>
        </div>
      )}

      <button data-testid="complete-mission-btn" disabled={!active || busy} onClick={() => setConfirm(true)}
        className="w-full flex items-center justify-center gap-2 border rounded-sm font-mono text-[11px] uppercase tracking-wider py-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/15">
        <CheckCircle2 size={13} /> Terminer la mission
      </button>

      <ConfirmDialog open={confirm} title="Terminer la mission"
        message="Clôturer la mission active ? La trajectoire réelle sera figée et la mission archivée dans l'historique post-vol."
        confirmLabel="Terminer" onCancel={() => setConfirm(false)} onConfirm={complete} testid="complete-mission-confirm" />
    </div>
  );
}
